import { capitalizeFirst } from "utils/Common/String";

export const SOV_STATUS = {
  ACTIVE: "active",
  SUPERSEDED: "superseded",
  PENDING: "pending",
} as const;

export type SovStatus = (typeof SOV_STATUS)[keyof typeof SOV_STATUS] | string;

export const LOCKED_SOV_STATUSES: string[] = [
  SOV_STATUS.ACTIVE,
  SOV_STATUS.SUPERSEDED,
  SOV_STATUS.PENDING,
];

export const isSOVLocked = (status: string): boolean =>
  LOCKED_SOV_STATUSES.includes(status);

export const getSOVStatusLabel = (status: string): string => {
  if (status === SOV_STATUS.ACTIVE) return "Published";
  if (status === SOV_STATUS.SUPERSEDED) return "Inactive";
  return status ? capitalizeFirst(status) : "-";
};

export const getSOVStatusColor = (status: string): string | undefined => {
  if (status === SOV_STATUS.ACTIVE) return "#1a6b35";
  if (status) return "var(--amber)";
  return undefined;
};

export const CONTRACT_AMOUNT = 1429690;

export const CURRENCY = {
  code: "USD",
  symbol: "$",
  name: "US Dollar",
};

export const quickAddDivisions = [
  {
    code: "0100",
    label: "DIV 01 - 0100",
    name: "General Requirements / General Conditions",
  },
  {
    code: "0200",
    label: "DIV 02 - 0200",
    name: "Existing Conditions / Sitework / Earthwork",
  },
  { code: "0210", label: "DIV 02 - 0210", name: "Paving & Curbing" },
  { code: "0300", label: "DIV 03 - 0300", name: "Concrete / Flatwork / Slabs" },
  { code: "0500", label: "DIV 05 - 0500", name: "Metals / Structural Steel" },
  {
    code: "0600",
    label: "DIV 06 - 0600",
    name: "Rough Carpentry / Wood Framing",
  },
  {
    code: "0700",
    label: "DIV 07 - 0700",
    name: "Thermal & Moisture / Roofing",
  },
  { code: "0800", label: "DIV 08 - 0800", name: "Openings / Doors & Windows" },
  {
    code: "0900",
    label: "DIV 09 - 0900",
    name: "Finishes / Drywall / Flooring / Paint",
  },
  { code: "1000", label: "DIV 10 - 1000", name: "Specialties / Equipment" },
  {
    code: "1500",
    label: "DIV 15 - 1500",
    name: "Mechanical / Plumbing / HVAC",
  },
  { code: "1600", label: "DIV 16 - 1600", name: "Electrical / Low Voltage" },
  {
    code: "1700",
    label: "DIV 17 - 1700",
    name: "Fire Protection / Suppression",
  },
  {
    code: "8000",
    label: "SOFT - 8000",
    name: "Soft Costs (Arch / Permits / Engineering)",
  },
  { code: "9000", label: "GC - 9000", name: "GC Overhead & Profit" },
  { code: "9900", label: "CONT - 9900", name: "Contingency / Allowances" },
];

export const initialRows = [
  {
    id: 1,
    csiCode: "0100",
    description: "General Conditions / Site Management",
    scheduledValue: 45000,
    vendor: "DeStefano & Associates",
    status: "complete",
  },
  {
    id: 2,
    csiCode: "0200",
    description: "Sitework / Earthwork / Excavation",
    scheduledValue: 195000,
    vendor: "Patrick's Landscaping LLC",
    status: "complete",
  },
  {
    id: 3,
    csiCode: "0210",
    description: "Paving & Curbing - Base & Finish",
    scheduledValue: 48750,
    vendor: "Dayton Sand & Gravel Co.",
    status: "complete",
  },
  {
    id: 4,
    csiCode: "0300",
    description: "Concrete / Flatwork / UG Slabs / Apron",
    scheduledValue: 92500,
    vendor: "Auburn Concrete",
    status: "complete",
  },
  {
    id: 5,
    csiCode: "0500",
    description: "Metals / Mechanical Screen / Steel Fabrication",
    scheduledValue: 38000,
    vendor: "FGF Construction Network Services",
    status: "complete",
  },
  {
    id: 6,
    csiCode: "0600",
    description: "Rough Carpentry / Wood Framing / Sheathing",
    scheduledValue: 285000,
    vendor: "BPI Construction Enterprises",
    status: "complete",
  },
  {
    id: 7,
    csiCode: "0700",
    description: "Roofing / Thermal & Moisture Protection",
    scheduledValue: 98500,
    vendor: "Kustom Construction",
    status: "complete",
  },
  {
    id: 8,
    csiCode: "0800",
    description: "Doors, Frames, Hardware & Glazing",
    scheduledValue: 64200,
    vendor: "Eastcoast Lumber & Supply",
    status: "complete",
  },
  {
    id: 9,
    csiCode: "1500",
    description: "Plumbing / Mechanical / HVAC Systems",
    scheduledValue: 112000,
    vendor: "SNH Mechanical",
    status: "complete",
  },
  {
    id: 10,
    csiCode: "1600",
    description: "Electrical Rough-In, Panels & Finish",
    scheduledValue: 188000,
    vendor: "Drinkwater & Sons Electric Inc.",
    status: "complete",
  },
  {
    id: 11,
    csiCode: "0300",
    description: "Precast / Structural Concrete - Footings & Foundation",
    scheduledValue: 42500,
    vendor: "Pepin Precast",
    status: "complete",
  },
  {
    id: 12,
    csiCode: "0900",
    description: "",
    scheduledValue: 0,
    vendor: "",
    status: "incomplete",
  },
  {
    id: 13,
    csiCode: "9000",
    description: "GC Overhead & Profit (8%)",
    scheduledValue: 114740,
    vendor: "DeStefano & Associates",
    vendorReadOnly: true,
    status: "complete",
  },
  {
    id: 14,
    csiCode: "9900",
    description: "Project Contingency / Incidentals",
    scheduledValue: 105500,
    vendor: "- (held by owner)",
    vendorReadOnly: true,
    status: "complete",
  },
];

export const tradeBreakdown = [
  {
    label: "Rough Carpentry / Framing",
    value: 285000,
    pct: 19.9,
    tone: "blue",
  },
  {
    label: "Sitework / Earthwork",
    value: 195000,
    pct: 13.6,
    tone: "blueLight",
  },
  { label: "Electrical", value: 188000, pct: 13.1, tone: "green" },
  { label: "GC OH&P (8%)", value: 114740, pct: 8, tone: "purple" },
  { label: "Contingency", value: 105500, pct: 7.4, tone: "red" },
  { label: "Plumbing / HVAC", value: 112000, pct: 7.8, tone: "teal" },
  { label: "Roofing / Envelope", value: 98500, pct: 6.9, tone: "amber" },
  { label: "All other trades", value: 330950, pct: 23.3, tone: "gray" },
];

export const successChecklist = [
  "14 line items across 8 CSI divisions",
  "Total SOV matches contract amount",
  "CSC New England notified for review",
  "TD Bank notified - Kolby Youland",
  "SOV PDF attached to project record",
];

export function formatCurrency(
  value: number | undefined | null,
  currencySymbol = CURRENCY.symbol,
): string {
  const num = Number(value || 0);
  const sign = num < 0 ? "-" : "";
  return `${sign}${currencySymbol}${Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function parseCurrencyInput(value: string | number): number {
  const cleanValue = String(value).replace(/[^0-9.-]+/g, "");
  const number = Number(cleanValue);
  if (Number.isNaN(number)) {
    return 0;
  }
  return number;
}
